import { useState, useEffect, useRef } from 'react';

// Today as 'YYYY-MM-DD' in LOCAL time. toISOString().split('T')[0] is UTC, so a
// signature captured after ~8pm Florida (UTC-4) recorded tomorrow's date.
// (signedAtFull keeps the full UTC ISO timestamp for the audit trail.)
const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function SignaturePad({ padRef, width = 500, height = 180, onDrawChange }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: (touch.clientX - rect.left) * (canvasRef.current.width / rect.width), y: (touch.clientY - rect.top) * (canvasRef.current.height / rect.height) };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    drawingRef.current = true;
  };

  const draw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = "#1a2740";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    if (!hasDrawn) setHasDrawn(true);
  };

  const endDraw = (e) => {
    if (e) e.preventDefault();
    drawingRef.current = false;
  };

  const clear = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setHasDrawn(false);
  };

  // Expose methods via ref + notify parent
  useEffect(() => {
    if (padRef) {
      padRef.current = {
        save: () => hasDrawn ? canvasRef.current.toDataURL("image/png") : null,
        clear,
        hasDrawn,
      };
    }
    if (onDrawChange) onDrawChange(hasDrawn);
  }, [hasDrawn]);

  return (
    <div style={{ position: "relative" }}>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        onMouseDown={startDraw}
        onMouseMove={draw}
        onMouseUp={endDraw}
        onMouseLeave={endDraw}
        onTouchStart={startDraw}
        onTouchMove={draw}
        onTouchEnd={endDraw}
        style={{
          width: "100%", maxWidth: width, height: height,
          border: "2px dashed #cbd5e1", borderRadius: 12,
          background: "#fff", cursor: "crosshair", touchAction: "none",
          display: "block",
        }}
      />
      <div style={{ position: "absolute", bottom: 16, left: 20, right: 20, borderTop: "1px solid #94a3b8", pointerEvents: "none" }} />
      {hasDrawn && (
        <button type="button" onClick={clear}
          style={{ position: "absolute", top: 8, right: 8, background: "#fee2e2", border: "1px solid #fca5a5", color: "#dc2626", fontSize: ".7rem", fontWeight: 700, padding: "4px 10px", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}>
          Clear
        </button>
      )}
    </div>
  );
}

export function PublicSigningPage({ signatures, onSignaturesChange, clients }) {
  const padRef = useRef(null);
  const [signerName, setSignerName] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sigRecord, setSigRecord] = useState(null);
  const [, forceUpdate] = useState(0); // to re-render when pad draws

  // Get signing token from URL hash: #sign/TOKEN
  const hash = window.location.hash || "";
  const tokenMatch = hash.match(/#sign\/(.+)/);
  const token = tokenMatch ? tokenMatch[1] : null;

  useEffect(() => {
    if (!token) return;
    const found = (signatures || []).find(s => s.signToken === token);
    if (found) {
      setSigRecord(found);
      const c = (clients || []).find(cl => cl.id === found.clientId);
      if (c) setSignerName(`${c.firstName || ""} ${c.lastName || ""}`.trim());
    }
  }, [token, signatures, clients]);

  if (!token || !sigRecord) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", fontFamily: "'Inter',system-ui,sans-serif", padding: "1rem" }}>
        <div style={{ textAlign: "center", maxWidth: 400 }}>
          <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>🔗</div>
          <h1 style={{ fontSize: "1.3rem", fontWeight: 800, color: "#1e293b", marginBottom: ".5rem" }}>
            {sigRecord?.status === "signed" ? "Document Already Signed" : "Invalid or Expired Link"}
          </h1>
          <p style={{ color: "#64748b", fontSize: ".9rem" }}>
            {sigRecord?.status === "signed"
              ? "This document has already been signed. Thank you."
              : "This signing link is invalid or has expired. Please contact your agent for a new link."}
          </p>
        </div>
      </div>
    );
  }

  if (sigRecord.status === "signed" || submitted) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#f0fdf4", fontFamily: "'Inter',system-ui,sans-serif", padding: "1rem" }}>
        <div style={{ textAlign: "center", maxWidth: 450 }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.5rem", fontSize: "2.5rem" }}>✅</div>
          <h1 style={{ fontSize: "1.5rem", fontWeight: 800, color: "#166534", marginBottom: ".5rem" }}>
            {sigRecord.lang === "es" ? "Documento Firmado" : "Document Signed"}
          </h1>
          <p style={{ color: "#4ade80", fontSize: ".95rem", marginBottom: "1.5rem" }}>
            {sigRecord.lang === "es"
              ? "Su firma ha sido registrada exitosamente. Puede cerrar esta ventana."
              : "Your signature has been recorded successfully. You may close this window."}
          </p>
          <div style={{ background: "#fff", border: "1px solid #bbf7d0", borderRadius: 12, padding: "1rem", fontSize: ".8rem", color: "#64748b", textAlign: "left" }}>
            <div><strong>{sigRecord.lang === "es" ? "Documento" : "Document"}:</strong> {sigRecord.type}</div>
            <div><strong>{sigRecord.lang === "es" ? "Firmante" : "Signer"}:</strong> {sigRecord.signerName || signerName}</div>
            <div><strong>{sigRecord.lang === "es" ? "Fecha" : "Date"}:</strong> {sigRecord.signedAt || new Date().toLocaleDateString("en-US")}</div>
          </div>
          <div style={{ marginTop: "2rem", fontSize: ".7rem", color: "#94a3b8" }}>
            Powered by <strong>Deskte</strong> &mdash; Electronic Signature Platform
          </div>
        </div>
      </div>
    );
  }

  const isEs = sigRecord.lang === "es";

  const handleSubmit = () => {
    const pad = padRef.current;
    if (!pad?.hasDrawn || !signerName.trim() || !agreed) return;
    const signatureImage = pad.save();
    if (!signatureImage) return;

    const signedRecord = {
      ...sigRecord,
      status: "signed",
      signedAt: todayLocal(),
      signedAtFull: new Date().toISOString(),
      signerName: signerName.trim(),
      signatureImage: signatureImage,
      signedUserAgent: navigator.userAgent,
      signedTimestamp: Date.now(),
    };

    onSignaturesChange(prev => prev.map(s => s.id === sigRecord.id ? signedRecord : s));
    setSubmitted(true);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter',system-ui,sans-serif" }}>
      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e2e8f0", padding: "1rem 1.5rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <img src="/logo/deskte-black.svg" alt="Deskte" style={{ height: 40, width: "auto", display: "block" }} />
        </div>
        <div style={{ fontSize: ".75rem", color: "#94a3b8", fontWeight: 600 }}>
          {isEs ? "Firma Electrónica Segura" : "Secure Electronic Signature"}
        </div>
      </div>

      <div style={{ maxWidth: 650, margin: "0 auto", padding: "2rem 1.25rem" }}>
        {/* Document Info */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "1.5rem", marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: ".5rem", marginBottom: "1rem" }}>
            <span style={{ fontSize: "1.3rem" }}>📄</span>
            <h1 style={{ fontSize: "1.15rem", fontWeight: 800, color: "#1e293b", margin: 0 }}>
              {sigRecord.type}
            </h1>
          </div>

          {/* Document body */}
          <div style={{
            background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10,
            padding: "1.25rem", fontSize: ".88rem", color: "#334155", lineHeight: 1.8,
            whiteSpace: "pre-line", maxHeight: 350, overflowY: "auto",
          }}>
            {(sigRecord.filledBody || "").replace(/\n\n_+\n.*(?:Firma|Signature)/g, "")}
          </div>
        </div>

        {/* Signature Section */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "1.5rem", marginBottom: "1.25rem" }}>
          <h2 style={{ fontSize: "1rem", fontWeight: 800, color: "#1e293b", marginBottom: ".25rem" }}>
            ✍️ {isEs ? "Su Firma" : "Your Signature"}
          </h2>
          <p style={{ fontSize: ".8rem", color: "#64748b", marginBottom: "1rem" }}>
            {isEs ? "Dibuje su firma en el recuadro de abajo usando su dedo o mouse." : "Draw your signature in the box below using your finger or mouse."}
          </p>

          <SignaturePad
            padRef={padRef}
            width={580}
            height={160}
            onDrawChange={() => forceUpdate(n=>n+1)}
          />
          {/* Hidden listener: canvas changes trigger re-render via onMouseUp below */}

          {/* Signer name */}
          <div style={{ marginTop: "1rem" }}>
            <label style={{ fontSize: ".75rem", fontWeight: 700, color: "#475569", display: "block", marginBottom: ".3rem" }}>
              {isEs ? "Nombre Completo del Firmante" : "Full Name of Signer"}
            </label>
            <input
              type="text"
              value={signerName}
              onChange={e => setSignerName(e.target.value)}
              placeholder={isEs ? "Escriba su nombre completo..." : "Type your full name..."}
              style={{
                width: "100%", padding: ".65rem .85rem", borderRadius: 10,
                border: "1px solid #cbd5e1", fontSize: ".9rem", fontFamily: "inherit",
                background: "#f8fafc", color: "#1e293b", boxSizing: "border-box",
              }}
            />
          </div>

          {/* Agreement checkbox */}
          <label style={{ display: "flex", alignItems: "flex-start", gap: ".6rem", marginTop: "1rem", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              style={{ marginTop: 3, width: 18, height: 18, accentColor: "#0ea5e9" }}
            />
            <span style={{ fontSize: ".82rem", color: "#475569", lineHeight: 1.5 }}>
              {isEs
                ? "Acepto que mi firma electrónica en este documento tiene la misma validez legal que una firma manuscrita, de acuerdo con la Ley ESIGN (15 U.S.C. §7001) y UETA."
                : "I agree that my electronic signature on this document has the same legal validity as a handwritten signature, in accordance with the ESIGN Act (15 U.S.C. §7001) and UETA."}
            </span>
          </label>
        </div>

        {/* Submit Button */}
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!padRef.current?.hasDrawn || !signerName.trim() || !agreed}
          style={{
            width: "100%", padding: "1rem", borderRadius: 12, border: "none",
            background: (!padRef.current?.hasDrawn || !signerName.trim() || !agreed)
              ? "#94a3b8"
              : "linear-gradient(135deg, #059669, #10b981)",
            color: "#fff", fontSize: "1rem", fontWeight: 800,
            cursor: (!padRef.current?.hasDrawn || !signerName.trim() || !agreed) ? "not-allowed" : "pointer",
            fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: ".5rem",
            boxShadow: (!padRef.current?.hasDrawn || !signerName.trim() || !agreed) ? "none" : "0 4px 20px rgba(5,150,105,.3)",
            transition: "all .2s",
          }}
        >
          ✍️ {isEs ? "Firmar Documento" : "Sign Document"}
        </button>

        {/* Legal footer */}
        <div style={{ marginTop: "1.5rem", textAlign: "center", fontSize: ".7rem", color: "#94a3b8", lineHeight: 1.6 }}>
          <p>{isEs
            ? "Al firmar, usted confirma que ha leído y entendido el documento anterior."
            : "By signing, you confirm that you have read and understood the document above."}</p>
          <p style={{ marginTop: ".5rem" }}>
            {isEs ? "Registro electrónico protegido" : "Electronic record protected"} &mdash; Powered by <strong>Deskte</strong> &copy; {new Date().getFullYear()} DALCERSERVICES LLC
          </p>
        </div>
      </div>
    </div>
  );
}
